import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Play, Sparkles, Zap, ToggleLeft, ToggleRight } from 'lucide-react';
import { useScriptStore } from '../../stores/scriptStore';
import { useUIStore } from '../../stores/uiStore';

export function ScriptsScreen() {
  const navigate = useNavigate();
  const { scripts, fetchScripts, startScript, toggleScript } = useScriptStore();
  const { addToast } = useUIStore();

  useEffect(() => {
    fetchScripts();
  }, [fetchScripts]);

  const handleRun = async (scriptId: string) => {
    await startScript(scriptId);
    addToast('Сценарий запущен', 'success');
  };

  const handleToggle = async (scriptId: string, currentState: boolean) => {
    await toggleScript(scriptId, !currentState);
    addToast(currentState ? 'Отключён' : 'Включён', 'success');
  };

  const activeCount = scripts.filter(s => s.isActive).length;

  return (
    <div className="min-h-screen pb-24">
      <div className="glass sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gradient">Сценарии</h1>
              <p className="text-sm text-slate-400 mt-0.5">
                {activeCount} из {scripts.length} активно
              </p>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/scripts/new')}
              className="w-11 h-11 gradient-primary rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/30"
            >
              <Plus className="w-5 h-5 text-white" />
            </motion.button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-6">
        {scripts.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-slate-300" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Нет сценариев</h3>
            <p className="text-slate-400 mb-6">Создайте автоматизацию для умного дома</p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/scripts/new')}
              className="gradient-primary text-white px-6 py-3 rounded-xl font-medium shadow-lg shadow-indigo-500/30"
            >
              Создать сценарий
            </motion.button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence mode="popLayout">
              {scripts.map((script, index) => (
                <motion.div
                  key={script.actionId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: index * 0.05 }}
                  className="glass-card-hover p-5 cursor-pointer"
                  onClick={() => navigate(`/scripts/edit/${script.actionId}`)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`
                        w-11 h-11 rounded-xl flex items-center justify-center
                        ${script.isActive ? 'gradient-success shadow-lg shadow-emerald-500/30' : 'bg-slate-100'}
                      `}>
                        <Zap className={`w-5 h-5 ${script.isActive ? 'text-white' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800">{script.name}</h3>
                        <p className={`text-xs ${script.isActive ? 'text-emerald-500' : 'text-slate-400'}`}>
                          {script.isActive ? 'Активен' : 'Отключён'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRun(script.actionId);
                        }}
                        className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-500 flex items-center justify-center hover:bg-indigo-100 transition-colors"
                      >
                        <Play className="w-5 h-5" />
                      </motion.button>
                      
                      <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggle(script.actionId, script.isActive);
                        }}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                          script.isActive 
                            ? 'bg-emerald-50 text-emerald-500' 
                            : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        {script.isActive 
                          ? <ToggleRight className="w-5 h-5" /> 
                          : <ToggleLeft className="w-5 h-5" />
                        }
                      </motion.button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-indigo-500 uppercase tracking-wider">Если</span>
                      <div className="flex-1 h-px bg-gradient-to-r from-indigo-200 to-transparent" />
                    </div>
                    <p className="text-sm text-slate-600 pl-2">
                      {script.ifDevices?.[0]?.deviceName || 'Условие'}
                    </p>
                    
                    <div className="flex items-center gap-2 mt-3">
                      <span className="text-xs font-semibold text-emerald-500 uppercase tracking-wider">То</span>
                      <div className="flex-1 h-px bg-gradient-to-r from-emerald-200 to-transparent" />
                    </div>
                    <p className="text-sm text-slate-600 pl-2">
                      {script.thenDevices?.[0]?.deviceName || 'Действие'}
                    </p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
